import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketStatus } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-statuses-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './statuses-form.component.html'
})
export class TicketsStatusesFormComponent implements OnDestroy {
  @Input() drawerMode = true;
  @Output() saved = new EventEmitter<TicketStatus>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  saving = false;
  error: string | null = null;
  isSystem = false;
  private originalName = '';

  // displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.maxLength(50)]],
    label: ['', [Validators.required, Validators.maxLength(100)]],
    color: ['#94a3b8', [Validators.maxLength(30)]],
    isDefault: [false],
    isResolved: [false],
    isTerminal: [false],
    isActive: [true]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(): void {
    this.isEditing = false;
    this.itemId = null;
    this.error = null;
    this.saving = false;
    this.isSystem = false;  
    this.form.reset({
      name: '',
      label: '',
      color: '#94a3b8',
      isDefault: false,
      isResolved: false,
      isTerminal: false,
      isActive: true
    });
    // por si el form se reutiliza en modo drawer, reactivar controles
    this.form.get('name')?.enable();
    this.form.get('isDefault')?.enable();
    this.form.get('isResolved')?.enable();
    this.form.get('isTerminal')?.enable();
  }

  loadStatus(id: number): void {
    this.isEditing = true;
    this.itemId = id;
    this.configApi.getStatusById(id).pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.isSystem = !!item.isSystem;

        this.form.patchValue({
          name: item.name,
          label: item.label,
          color: item.color || '#94a3b8',
          isDefault: item.isDefault,
          isResolved: item.isResolved,
          isTerminal: item.isTerminal,
          isActive: item.isActive
        });
        this.originalName = item.label;
        // estados de sistema — bloquear Nombre interno y flags estructurales.
        // Label, Color, Orden y Activo siguen editables.
        if (this.isSystem) {
          this.form.get('name')?.disable();
          this.form.get('isDefault')?.disable();
          this.form.get('isResolved')?.disable();
          this.form.get('isTerminal')?.disable();
        } else {
          this.form.get('name')?.enable();
          this.form.get('isDefault')?.enable();
          this.form.get('isResolved')?.enable();
          this.form.get('isTerminal')?.enable();
        }
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el estado';
      }
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.error = null;

    const raw = this.form.getRawValue();
    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const req = {
      name: raw.name,
      label: raw.label,
      color: raw.color || undefined,
      isDefault: !!raw.isDefault,
      isResolved: !!raw.isResolved,
      isTerminal: !!raw.isTerminal,
      isActive: !!raw.isActive
    };

    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateStatus(this.itemId, req)
      : this.configApi.createStatus(req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Estado',
          this.originalName !== (raw.label?.trim() || '')
            ? `Se actualizó "${this.originalName}" → "${raw.label?.trim() || ''}".`
            : `Se actualizó "${raw.label?.trim() || ''}" correctamente.`);
        else this.toast.created('Estado', `Se creó "${raw.label?.trim() || ''}" correctamente.`);
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el estado';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void {
    this.cancelled.emit();
  }
}
